import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ListItems } from "@pages:app/docs/Components/ListItems";
import { extractEAR } from "@pages:app/docs/docsHelper";
import { FountainBlue } from "@themes:palette";
import { noop } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type TextSummaryProps = {
  textSummary: string;
  textEAR: string; // EAR == entities and relationships
  inPulseSummary: boolean;
  inPulseEAR: boolean;
  dataCyList?: string;
};

export const TextSummaryResult = ({
  textSummary,
  textEAR,
  inPulseSummary,
  inPulseEAR,
}: TextSummaryProps) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState({
    summary: false,
    EAR: false,
  });
  return (
    <Box
      sx={{ marginTop: 10, width: "50%", marginX: "auto", marginBottom: 10 }}
    >
      <Card
        sx={{
          paddingLeft: 2,
          paddingRight: 2,
          borderRadius: "1rem",
          border: "2px solid #E4F2F2",
        }}
        variant="outlined"
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">{t("docs.aiSummary")}</Typography>
            <Tooltip
              title={
                copySuccess.summary
                  ? t("common.copied")
                  : t("common.copyToClipboard")
              }
              placement={"top"}
            >
              <IconButton
                sx={{
                  color: (theme) => theme.palette.grey[400],
                  "&:hover": {
                    color: FountainBlue[500],
                  },
                }}
                disabled={inPulseSummary}
                onClick={() => {
                  if (!copySuccess.summary) {
                    navigator.clipboard
                      .writeText(textSummary || "")
                      .then(() => {
                        setCopySuccess((prevState) => ({
                          ...prevState,
                          summary: true,
                        }));
                        setTimeout(() => {
                          setCopySuccess((prevState) => ({
                            ...prevState,
                            summary: false,
                          }));
                        }, 2000);
                      }, noop);
                  }
                }}
              >
                {copySuccess.summary ? (
                  <SVGInternalIcon
                    icon="checkIcon"
                    sx={{ width: 24, height: 24 }}
                  />
                ) : (
                  <SVGInternalIcon
                    icon="copyIcon"
                    sx={{ width: 24, height: 24 }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Divider
            sx={{
              borderColor: "#E4F2F2",
              marginTop: 2,
            }}
          />
          {inPulseSummary ? (
            <Box sx={{ width: "100%", marginTop: "1.25rem" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography
              sx={{
                marginTop: "1.25rem",
                lineHeight: "1.75rem",
                textAlign: "left",
              }}
            >
              {textSummary}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "2.5rem",
            }}
          >
            <Typography variant="h5">
              {t("docs.entitiesAndRelationships")}
            </Typography>
            <Tooltip
              title={
                copySuccess.EAR
                  ? t("common.copied")
                  : t("common.copyToClipboard")
              }
              placement={"top"}
            >
              <IconButton
                sx={{
                  color: (theme) => theme.palette.grey[400],
                  "&:hover": {
                    color: FountainBlue[500],
                  },
                }}
                disabled={inPulseEAR}
                onClick={() => {
                  if (!copySuccess.EAR) {
                    navigator.clipboard.writeText(textEAR || "").then(() => {
                      setCopySuccess((prevState) => ({
                        ...prevState,
                        EAR: true,
                      }));
                      setTimeout(
                        () =>
                          setCopySuccess((prevState) => ({
                            ...prevState,
                            EAR: false,
                          })),
                        2000,
                      );
                    }, noop);
                  }
                }}
              >
                {copySuccess.EAR ? (
                  <SVGInternalIcon
                    icon="checkIcon"
                    sx={{ width: 24, height: 24 }}
                  />
                ) : (
                  <SVGInternalIcon
                    icon="copyIcon"
                    sx={{ width: 24, height: 24 }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ borderColor: "#E4F2F2", marginTop: 2 }} />
          {inPulseEAR ? (
            <Box
              sx={{
                width: "100%",
                marginTop: "1.25rem",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <ListItems
                title={t("docs.entities")}
                items={extractEAR(textEAR).entities}
              />
              <ListItems
                title={t("docs.relationships")}
                items={extractEAR(textEAR).relationships}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
